import React from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'
import { QueryListFilter } from '@feedyou/schema'

import { IconButton } from '../../../../../components/Styleguide/Button'
import { Preset, getDefaultDateFilterTimestamps, getPresetOptionForDateTimeFilter } from './utils'

interface Props {
  activeFilter: QueryListFilter
  uncommitedFilter: QueryListFilter
  presetOption: { value: Preset | undefined; setValue: (value: Preset) => void }

  setTimestampFrom(timestamp: number): void
  setTimestampTo: (timestamp: number) => void
}

export const DateTimeOptions: React.FC<Props> = ({
  presetOption,
  activeFilter,
  uncommitedFilter,
  setTimestampFrom,
  setTimestampTo
}) => {
  if (presetOption.value === 'custom') {
    return (
      <DateTimePicker>
        <Title>from</Title>
        <input
          id="dateFrom"
          name="dateFrom"
          type="date"
          onKeyDown={event => event.preventDefault()}
          onChange={event => setTimestampFrom(dayjs(event.target.value).startOf('days').valueOf())}
          value={dayjs(
            Number(
              uncommitedFilter.fromTimestamp ||
                activeFilter.fromTimestamp ||
                getDefaultDateFilterTimestamps().fromTimestamp
            )
          ).format('YYYY-MM-DD')}
        />
        <Title>to</Title>
        <input
          id="dateTo"
          name="dateTo"
          type="date"
          onKeyDown={event => event.preventDefault()}
          onChange={event =>
            setTimestampTo(dayjs(event.target.value).add(1, 'days').startOf('days').valueOf())
          }
          value={dayjs(
            Number(
              uncommitedFilter.toTimestamp ||
                activeFilter.toTimestamp ||
                getDefaultDateFilterTimestamps().toTimestamp
            )
          )
            .subtract(1, 'seconds')
            .endOf('days')
            .format('YYYY-MM-DD')}
        />
        <CloseIcon>
          <IconButton
            iconName="times"
            tooltipText="Cancel"
            tooltipPlacement="top"
            onClick={() => {
              const presetOptionForFilter = getPresetOptionForDateTimeFilter(activeFilter)
              if (presetOptionForFilter === 'custom') {
                presetOption.setValue('0')
                const defaultTimestamps = getDefaultDateFilterTimestamps()
                setTimestampTo(defaultTimestamps.toTimestamp)
                setTimestampFrom(defaultTimestamps.fromTimestamp)
                return
              }

              presetOption.setValue(presetOptionForFilter)
              setTimestampTo(dayjs(Number(activeFilter.toTimestamp)).valueOf())
              setTimestampFrom(dayjs(Number(activeFilter.fromTimestamp)).valueOf())
            }}
          />
        </CloseIcon>
      </DateTimePicker>
    )
  }

  return (
    <select
      id="date"
      name="date"
      defaultValue="0"
      value={presetOption.value}
      onKeyDown={event => event.preventDefault()}
      onChange={event => {
        const optionValue = event.target.value as Preset
        presetOption.setValue(optionValue)
        if (optionValue === 'custom') return
        const isYesterdayOption = optionValue === '1'
        const defaultStamps = getDefaultDateFilterTimestamps()
        const stampFrom = dayjs().subtract(Number(optionValue), 'days').startOf('days').valueOf()
        const stampTo = isYesterdayOption ? defaultStamps.fromTimestamp : defaultStamps.toTimestamp

        setTimestampTo(stampTo)
        setTimestampFrom(stampFrom)
      }}
    >
      <option value="0">Today</option>
      <option value="1">Yesterday</option>
      <option value="3">Last 3 days</option>
      <option value="7">Last 7 days</option>
      <option value="14">Last 14 days</option>
      <option value="30">Last 30 days</option>
      <option value="90">Last 90 days</option>
      <option value="custom">Custom</option>
    </select>
  )
}

const Title = styled.div`
  font-weight: 500;
  margin: auto 5px;
  white-space: nowrap;
`

const DateTimePicker = styled.div`
  display: flex;
`

const CloseIcon = styled.div`
  cursor: pointer;
  margin-left: 6px;
`
